import React from 'react';
import { StandardPdfTemplate } from '../../templates/StandardPdfTemplate';
import { ProjectData, ProjectInfoData } from '../../shared/types/PdfCommonTypes';
import { TFunction } from 'i18next';
import { unit as unitEnum } from '../../../../enum/project';
import { PavedResultsCore } from './sections/PavedResultsCore';

interface PavedResultsPdfDocumentProps {
    output: any;
    projectInfo: any;
    dynamicProject: any;
    tireImageUnreinforced: string;
    tireImageReinforced: string;
    tireImageGeogrid: string;
    chartImage: string;
    t: TFunction;
    trafficData?: {
        unstabilized?: any;
        increasedPassages?: any;
        optimized?: any;
    };
    // Dynamic design sections - optional for backward compatibility
    surfaceDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
    };
    baseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
    };
    subbaseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
    };
}

const PavedResultsPdfDocumentV2: React.FC<PavedResultsPdfDocumentProps> = ({
    output,
    projectInfo,
    dynamicProject,
    tireImageUnreinforced,
    tireImageReinforced,
    tireImageGeogrid,
    chartImage,
    t,
    trafficData,
    surfaceDesignImages,
    baseDesignImages,
    subbaseDesignImages,
}) => {
    const unitSystem = dynamicProject?.data?.unit === 'Imperial' ? unitEnum.Imperial : unitEnum.SI;
    const projectType = 'paved';

    // Extract project information
    const projectData = dynamicProject?.data;
    const projectInfoData = projectData?.projectInfo;
    const projectMapImage = projectData?.mapImage;

    // Prepare data for StandardPdfTemplate
    const projectDataForTemplate: ProjectData = {
        projectName: projectData?.projectName || 'N/A',
        projectType: projectType,
        unit: projectInfo?.unit || projectData?.unit || (unitSystem === unitEnum.Imperial ? 'Imperial' : 'Metric'),
        mapImage: projectMapImage,
    };

    const projectInfoDataForTemplate: ProjectInfoData = {
        data: {
            nrProject: projectInfo?.nrProject || projectInfoData?.data?.nrProject || 'N/A',
        },
        projectOwner: projectInfo?.projectOwner || projectInfoData?.projectOwner || 'N/A',
        designerFirmName: projectInfo?.designerFirmName || projectInfoData?.designerFirmName || 'N/A',
        designerFirmCountry: projectInfo?.designerFirmCountry || projectInfoData?.designerFirmCountry || 'N/A',
        designerFirmCity: projectInfo?.designerFirmCity || projectInfoData?.data?.designerFirmCity || 'N/A',
        coordinates: projectInfo?.coordinates || projectInfoData?.coordinates || [null, null],
    };

    return (
        <StandardPdfTemplate
            projectType={projectType}
            projectData={projectDataForTemplate}
            projectInfoData={projectInfoDataForTemplate}
            t={t}
            headerConfig={{
                customTitle: t('pdf_header_technical_report_groundmac') || 'GROUNDMAC - Design Technical Report',
                customProjectType: t('project_type_road_paved') || 'Road Paved',
                customSubtitle: t('subtitle_aashto') || 'Designing AASHTO METHOD',
                showGeogridSubtitle: false,
            }}
            footerConfig={{
                customText: t('pdf_footer') || 'MACread eDesign',
                customNotice: t('pdf_footer_notice') || 'Footer notice',
            }}
            showProjectInfo={true}>
            {/* Use the shared core component */}
            <PavedResultsCore
                output={output}
                projectData={projectData}
                tireImageUnreinforced={tireImageUnreinforced}
                tireImageReinforced={tireImageReinforced}
                tireImageGeogrid={tireImageGeogrid}
                chartImage={chartImage}
                trafficData={trafficData}
                surfaceDesignImages={surfaceDesignImages}
                baseDesignImages={baseDesignImages}
                subbaseDesignImages={subbaseDesignImages}
                t={t}
                showInputData={true}
                showChart={true}
                showResults={true}
            />
        </StandardPdfTemplate>
    );
};

export default PavedResultsPdfDocumentV2;
